import * as THREE from 'three';

export class Player {
    constructor(scene, camera, audioManager, world) {
        this.scene = scene;
        this.camera = camera;
        this.audioManager = audioManager;
        this.world = world; // Store world reference for terrain collision
        
        // Player stats
        this.health = 100;
        this.maxHealth = 100;
        this.essence = 0;
        this.evolutionLevel = 0;
        this.invulnerable = false;
        this.isDead = false;
        this.lives = 3; // 3 lives system
        this.maxLives = 3;
        
        // Movement
        this.velocity = new THREE.Vector3();
        this.speed = 10;
        this.jumpForce = 8;
        this.isGrounded = false;
        this.gravity = -25;
        
        // Combat
        this.projectiles = [];
        this.fireRate = 0.3;
        this.lastFireTime = 0;
        this.projectileSpeed = 30;
        this.damage = 20;
        this.hasMachineGun = false; // Machine gun hands upgrade
        
        // Evolution traits
        this.traits = {
            tentacles: false,
            wings: false,
            armor: false,
            toxicSpikes: false,
            regeneration: false,
            machineGun: false,
            shock: false
        };
        
        // Shock damage tracking
        this.recentlyHitEnemies = []; // Track enemies hit in last 2 seconds
        
        // Create player mesh (spore creature)
        this.createMesh();
        
        // Create gun model attached to camera
        this.createGunModel();
        
        // Position - spawn away from volcano (which is at 0,0,0)
        this.camera.position.set(40, 2, 40);
        this.mesh.position.set(40, 2, 40);
    }
    
    createMesh() {
        // Create a blobby spore creature
        const geometry = new THREE.SphereGeometry(0.8, 16, 16);
        const material = new THREE.MeshStandardMaterial({
            color: 0x00ff88,
            emissive: 0x00ff44,
            emissiveIntensity: 0.3,
            roughness: 0.6,
            metalness: 0.2
        });
        
        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.castShadow = true;
        this.scene.add(this.mesh);
    }
    
    createGunModel() {
        // Create simple gun model with cylinders
        const gunGroup = new THREE.Group();
        
        // Barrel (main cylinder)
        const barrelGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.4, 8);
        const barrelMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x333333,
            metalness: 0.8,
            roughness: 0.2
        });
        const barrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
        barrel.rotation.z = Math.PI / 2; // Point forward
        barrel.position.set(0.15, 0, 0);
        gunGroup.add(barrel);
        
        // Handle (cylinder)
        const handleGeometry = new THREE.CylinderGeometry(0.025, 0.025, 0.15, 8);
        const handleMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x222222,
            metalness: 0.6,
            roughness: 0.4
        });
        const handle = new THREE.Mesh(handleGeometry, handleMaterial);
        handle.rotation.z = Math.PI / 4; // Angled grip
        handle.position.set(-0.05, -0.1, 0);
        gunGroup.add(handle);
        
        // Receiver (box for body)
        const receiverGeometry = new THREE.BoxGeometry(0.15, 0.06, 0.06);
        const receiver = new THREE.Mesh(receiverGeometry, barrelMaterial);
        receiver.position.set(0, 0, 0);
        gunGroup.add(receiver);
        
        // Position gun in lower right of screen
        gunGroup.position.set(0.25, -0.20, -0.5); // Right, below center, in front of camera
        gunGroup.rotation.y = -Math.PI / 12; // Slight angle inward
        
        // Attach to camera so it moves with view
        this.camera.add(gunGroup);
        this.gunModel = gunGroup;
        
        // Create laser pointer (hidden by default)
        const laserGeometry = new THREE.BufferGeometry();
        const laserMaterial = new THREE.LineBasicMaterial({ 
            color: 0xff0000,
            transparent: true,
            opacity: 0.6
        });
        this.laserLine = new THREE.Line(laserGeometry, laserMaterial);
        this.laserLine.visible = false;
        this.scene.add(this.laserLine);
    }
    
    updateLaser() {
        // Only show laser if machine gun trait is active
        if (!this.traits.machineGun) {
            this.laserLine.visible = false;
            return;
        }
        
        this.laserLine.visible = true;
        
        // CRITICAL: Update gun model's world matrix before getting world position
        this.gunModel.updateMatrixWorld(true);
        
        // Get gun barrel world position
        const gunBarrelLocal = new THREE.Vector3(0.35, 0, 0);
        const gunBarrelWorld = gunBarrelLocal.clone();
        this.gunModel.localToWorld(gunBarrelWorld);
        
        // Raycast from gun barrel in camera direction
        const raycaster = new THREE.Raycaster();
        const direction = new THREE.Vector3();
        this.camera.getWorldDirection(direction);
        raycaster.set(gunBarrelWorld, direction);
        
        // Raycast to find hit point (or max range)
        const maxRange = 200;
        const endPoint = gunBarrelWorld.clone().add(direction.multiplyScalar(maxRange));
        
        // Update laser line geometry
        const positions = new Float32Array([
            gunBarrelWorld.x, gunBarrelWorld.y, gunBarrelWorld.z,
            endPoint.x, endPoint.y, endPoint.z
        ]);
        this.laserLine.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.laserLine.geometry.attributes.position.needsUpdate = true;
    }
    
    useTentacleGrab(targetPosition) {
        // Load tentacle texture
        const textureLoader = new THREE.TextureLoader();
        const tentacleTexture = textureLoader.load('/textures/tentacle.png');
        
        // Create tentacle cylinder
        const distance = this.camera.position.distanceTo(targetPosition);
        const tentacleGeometry = new THREE.CylinderGeometry(0.15, 0.2, 1, 16); // Start small
        const tentacleMaterial = new THREE.MeshStandardMaterial({
            map: tentacleTexture,
            color: 0x00ff44,
            emissive: 0x00aa22,
            emissiveIntensity: 0.3,
            roughness: 0.6,
            metalness: 0.2
        });
        
        const tentacle = new THREE.Mesh(tentacleGeometry, tentacleMaterial);
        
        // Position at bottom left of camera view
        const startPos = this.camera.position.clone();
        const forward = new THREE.Vector3();
        const right = new THREE.Vector3();
        this.camera.getWorldDirection(forward);
        right.crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
        
        // Start from bottom left
        startPos.add(right.multiplyScalar(-1.5)); // Left
        startPos.y -= 2; // Below camera
        startPos.add(forward.multiplyScalar(0.5)); // Slightly forward
        
        tentacle.position.copy(startPos);
        this.scene.add(tentacle);
        
        // Animate tentacle extending to target
        const startTime = performance.now();
        const duration = 300; // 300ms to extend
        
        const animate = () => {
            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Smooth easing
            const eased = progress < 0.5 
                ? 2 * progress * progress 
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            
            // Extend tentacle length
            const currentLength = 1 + (distance * eased);
            tentacle.scale.y = currentLength;
            
            // Point tentacle at target
            const direction = new THREE.Vector3();
            direction.subVectors(targetPosition, startPos);
            const midPoint = startPos.clone().add(direction.multiplyScalar(0.5 * eased));
            tentacle.position.copy(midPoint);
            tentacle.lookAt(targetPosition);
            tentacle.rotateX(Math.PI / 2); // Cylinder is vertical by default
            
            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                // Retract after reaching target
                setTimeout(() => {
                    const retractStart = performance.now();
                    const retractDuration = 200;
                    
                    const retract = () => {
                        const retractElapsed = performance.now() - retractStart;
                        const retractProgress = Math.min(retractElapsed / retractDuration, 1);
                        
                        tentacle.scale.y = currentLength * (1 - retractProgress);
                        tentacle.material.opacity = 1 - retractProgress;
                        tentacle.material.transparent = true;
                        
                        if (retractProgress < 1) {
                            requestAnimationFrame(retract);
                        } else {
                            this.scene.remove(tentacle);
                        }
                    };
                    retract();
                }, 100); // Hold for 100ms
            }
        };
        animate();
        
        return true;
    }
    
    moveForward(distance) {
        const direction = new THREE.Vector3();
        this.camera.getWorldDirection(direction);
        direction.y = 0;
        direction.normalize();
        this.velocity.x = direction.x * this.speed * distance;
        this.velocity.z = direction.z * this.speed * distance;
    }
    
    moveRight(distance) {
        const direction = new THREE.Vector3();
        this.camera.getWorldDirection(direction);
        direction.y = 0;
        direction.normalize();
        const right = new THREE.Vector3();
        right.crossVectors(direction, new THREE.Vector3(0, 1, 0));
        this.velocity.x = right.x * this.speed * distance;
        this.velocity.z = right.z * this.speed * distance;
    }
    
    jump() {
        if (this.isGrounded) {
            this.velocity.y = this.jumpForce;
            this.isGrounded = false;
            if (this.audioManager) {
                this.audioManager.playJump();
            }
        }
    }
    
    shoot() {
        const currentTime = Date.now() / 1000;
        
        // Machine gun mode: 10x faster fire rate!
        const actualFireRate = this.traits.machineGun ? this.fireRate / 10 : this.fireRate;
        
        if (currentTime - this.lastFireTime < actualFireRate) return;
        
        this.lastFireTime = currentTime;
        
        if (this.audioManager) {
            this.audioManager.playShoot();
        }
        
        // Machine gun: shoot from both hands!
        const shotsToFire = this.traits.machineGun ? 2 : 1;
        
        // SAFETY: Cap total projectiles to prevent memory issues
        if (this.projectiles.length >= 100) {
            // Remove oldest projectiles
            const toRemove = this.projectiles.length - 90;
            for (let i = 0; i < toRemove; i++) {
                const old = this.projectiles.shift();
                if (old) this.scene.remove(old);
            }
        }
        
        for (let i = 0; i < shotsToFire; i++) {
            // Create projectile - BIGGER and BRIGHTER for visibility
            const geometry = new THREE.SphereGeometry(0.3, 8, 8); // Doubled size!
            const projectileColor = this.traits.machineGun ? 0x000000 : (this.traits.toxicSpikes ? 0xff00ff : 0x00ffff);
            const material = new THREE.MeshStandardMaterial({
                color: projectileColor,
                emissive: projectileColor,
                emissiveIntensity: this.traits.machineGun ? 0 : 1.5, // Black for machine gun
                metalness: 0,
                roughness: 0.5
            });
            
            const projectile = new THREE.Mesh(geometry, material);
            
            // Force matrix update since gun is camera child - prevents spawn from origin
            this.gunModel.updateMatrixWorld(true);
            
            // Get gun barrel world position (end of barrel)
            const gunBarrelLocal = new THREE.Vector3(0.35, 0, 0); // Right side, forward of gun model
            const gunBarrelWorld = this.gunModel.localToWorld(gunBarrelLocal);
            
            // Position projectile at gun barrel
            projectile.position.copy(gunBarrelWorld);
            
            // Hide projectile initially (will show once 1+ unit away)
            projectile.visible = false;
            projectile.spawnPosition = projectile.position.clone();
            projectile.spawnTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
            
            // Initialize previous position for CCD
            projectile.prevPosition = projectile.position.clone();
            
            if (shotsToFire === 2) {
                // Offset left/right for dual hand shooting
                const rightVector = new THREE.Vector3();
                this.camera.getWorldDirection(rightVector);
                rightVector.cross(new THREE.Vector3(0, 1, 0)).normalize();
                projectile.position.add(rightVector.multiplyScalar(i === 0 ? -0.3 : 0.3));
            }
            
            // Set velocity in camera direction with slight spread for machine gun
            const direction = new THREE.Vector3();
            this.camera.getWorldDirection(direction);
            
            if (this.traits.machineGun) {
                // Add slight random spread for machine gun
                direction.x += (Math.random() - 0.5) * 0.05;
                direction.y += (Math.random() - 0.5) * 0.05;
                direction.z += (Math.random() - 0.5) * 0.05;
                direction.normalize();
            }
            
            // Machine gun bullets are 3x faster!
            const speed = this.traits.machineGun ? this.projectileSpeed * 3 : this.projectileSpeed;
            projectile.velocity = direction.multiplyScalar(speed);
            
            this.projectiles.push(projectile);
            this.scene.add(projectile);
        }
    }
    
    takeDamage(amount) {
        // FORCE damage through
        const actualDamage = this.traits.armor ? amount * 0.5 : amount;
        this.health -= actualDamage;
        if (this.health < 0) this.health = 0;
        
        // Don't log in production (performance)
        // log(`🩸 PLAYER TOOK DAMAGE: ${actualDamage}, HP now: ${this.health}/${this.maxHealth}`);
        
        // RED BORDER FLASH
        const healthPercent = (this.health / this.maxHealth) * 100;
        const vignette = document.getElementById('damage-vignette');
        if (vignette) {
            if (healthPercent < 30) {
                vignette.style.borderWidth = '20px';
                vignette.style.borderColor = 'rgba(255, 0, 0, 1.0)';
            } else {
                vignette.style.borderWidth = '8px';
                vignette.style.borderColor = 'rgba(255, 0, 0, 0.8)';
            }
            vignette.classList.add('active');
            setTimeout(() => {
                vignette.classList.remove('active');
            }, 100);
        }
        
        if (this.health <= 0) {
            this.die();
        }
    }
    
    die() {
        console.log('Player died! Game Over');
        this.isDead = true;
        
        // PLAYER GIBLET EXPLOSION! (green for spore, optimized count)
        if (window.game) {
            window.game.createGibletExplosion(this.mesh.position, 0x00ff88, 10);
        }
        
        // Trigger game over
        if (window.game) {
            window.game.handlePlayerDeath();
        }
    }
    
    addEssence(amount) {
        this.essence += amount;
        if (window.game && typeof amount === 'number') {
            window.game.totalEssenceEarned = (window.game.totalEssenceEarned || 0) + amount;
        }
    }
    
    evolve(trait) {
        if (this.traits[trait] !== undefined) {
            this.traits[trait] = true;
            this.updateAppearance();
        }
    }
    
    updateAppearance() {
        // Update mesh based on evolution
        const color = this.traits.toxicSpikes ? 0xff00ff : 0x00ff88;
        this.mesh.material.color.setHex(color);
        
        // Scale with armor
        if (this.traits.armor) {
            this.mesh.scale.set(1.2, 1.2, 1.2);
        }
    }
    
    getTerrainHeight(position) {
        if (!this.world) return 0; // Fallback if no world
        
        // Create raycaster shooting down from high above the position
        const raycaster = new THREE.Raycaster();
        const rayOrigin = new THREE.Vector3(position.x, 100, position.z); // Start high up
        const rayDirection = new THREE.Vector3(0, -1, 0); // Shoot down
        
        raycaster.set(rayOrigin, rayDirection);
        
        // Check all terrain meshes
        const terrainMeshes = [];
        
        // Add island terrain
        if (this.world.island) {
            terrainMeshes.push(this.world.island);
        }
        
        // Add volcano terrain
        if (this.world.volcano && this.world.volcano.terrain) {
            terrainMeshes.push(this.world.volcano.terrain);
        }
        
        // Add beach
        if (this.world.beach) {
            terrainMeshes.push(this.world.beach);
        }
        
        // Raycast to find intersections
        const intersects = raycaster.intersectObjects(terrainMeshes, false);
        
        if (intersects.length > 0) {
            // Return the highest intersection point (in case of overlapping terrain)
            return Math.max(...intersects.map(hit => hit.point.y));
        }
        
        // Fallback to flat ground level
        return 0;
    }
    
    update(deltaTime) {
        // Apply gravity
        if (!this.isGrounded) {
            this.velocity.y += this.gravity * deltaTime;
        }
        
        // Update position
        this.camera.position.x += this.velocity.x * deltaTime;
        this.camera.position.y += this.velocity.y * deltaTime;
        this.camera.position.z += this.velocity.z * deltaTime;
        
        // Ground check with raycasting for terrain (including volcano!)
        const groundHeight = this.getTerrainHeight(this.camera.position);
        const playerHeight = 2; // Player's height above ground
        
        if (this.camera.position.y <= groundHeight + playerHeight) {
            this.camera.position.y = groundHeight + playerHeight;
            this.velocity.y = 0;
            this.isGrounded = true;
        } else {
            this.isGrounded = false;
        }
        
        // Sync mesh with camera
        this.mesh.position.copy(this.camera.position);
        
        // Update projectiles - ONLY MOVE THEM, DON'T REMOVE
        // Collision detection will handle all removal
        for (let i = this.projectiles.length - 1; i >= 0; i--) {
            const projectile = this.projectiles[i];
            if (!projectile) {
                this.projectiles.splice(i, 1);
                continue;
            }
            // Store previous position for CCD
            const prev = projectile.position.clone();
            projectile.position.add(projectile.velocity.clone().multiplyScalar(deltaTime));
            projectile.prevPosition = prev;
            
            // Make projectile visible once 1+ unit away from spawn
            if (!projectile.visible && projectile.spawnPosition) {
                const distanceFromSpawn = projectile.position.distanceTo(projectile.spawnPosition);
                if (distanceFromSpawn >= 1) {
                    projectile.visible = true;
                }
            }
        }
        
        // Regeneration trait
        if (this.traits.regeneration && this.health < this.maxHealth) {
            this.health = Math.min(this.maxHealth, this.health + 5 * deltaTime);
        }
        
        // Update laser pointer (machine gun only)
        this.updateLaser();
        
        // Decay velocity (friction)
        this.velocity.x *= 0.9;
        this.velocity.z *= 0.9;
    }
}
